import React, { useCallback, useRef, useState } from 'react';
import {
  TradingScoreWidgetInstanceConfig,
  TradingScoreDataContent,
  TradingScoreValue,
  TRADING_SCORE_ROWS,
  TRADING_SCORE_STYLES,
} from './types';
import { WidgetEmptyState } from '../../../design';

interface TradingScoreWidgetRendererProps {
  config: TradingScoreWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  onDataChange?: (dataKey: string, newData: TradingScoreDataContent) => void;
  readOnly?: boolean;
}

const TradingScoreWidgetRenderer: React.FC<TradingScoreWidgetRendererProps> = ({
  config,
  dashboardData = {},
  onDataChange,
  readOnly = false,
}) => {
  const data = dashboardData[config.dataKey] as TradingScoreDataContent | undefined;
  const [localAction, setLocalAction] = useState<TradingScoreValue | undefined>(data?.action);
  const lastPropAction = useRef(data?.action);

  if (data?.action !== lastPropAction.current) {
    lastPropAction.current = data?.action;
    setLocalAction(data?.action);
  }

  const handleSelect = useCallback(
    (value: TradingScoreValue) => {
      if (readOnly || !onDataChange) return;
      setLocalAction(value);
      onDataChange(config.dataKey, { action: value });
    },
    [config.dataKey, onDataChange, readOnly],
  );

  if (!config.dataKey) {
    return (
      <WidgetEmptyState
        title="No data key configured"
        description="Configure a data key in the widget settings."
      />
    );
  }

  return (
    <div className="h-full bg-surface p-3 sm:p-4 flex items-center justify-center">
      <div className="w-full max-w-md flex flex-col gap-2">
        {TRADING_SCORE_ROWS.map((row) => (
          <div key={row.label || '_neutral'} className="flex items-center gap-2">
            <span className="w-10 shrink-0 text-[11px] font-medium text-neutral-500 uppercase tracking-wide">
              {row.label}
            </span>
            <div className="flex-1 grid grid-cols-2 gap-2">
              {row.options.map((option) => {
                const isActive = localAction === option.value;
                const styles = TRADING_SCORE_STYLES[option.value];
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSelect(option.value)}
                    disabled={readOnly}
                    aria-pressed={isActive}
                    className={[
                      'h-10 rounded-lg border px-3 text-xs sm:text-sm font-semibold text-center transition-all duration-150',
                      isActive ? styles.active : styles.idle,
                      readOnly ? 'cursor-default opacity-80' : 'cursor-pointer',
                    ].join(' ')}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default React.memo(TradingScoreWidgetRenderer);
